import { OktaAuth } from '@okta/okta-auth-js';
import { AxiosError } from 'axios';
import { useCallback } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { AppDispatch } from '../app/store';
import { setCredentialsAndUser } from '../features/userSlice';
import { Logger } from '../logger';
import { UserService } from '../services/user/user.service';

type LoginHandlerResult = {
    handleLogin: (accessToken: string) => Promise<void>;
};

/**
 * Custom hook to handle common login logic including:
 * - Fetching user details
 * - Saving to Redux/localStorage
 * - Navigation after successful login
 */
export const useLoginHandler = (
    dispatch: AppDispatch,
    navigate: NavigateFunction,
    oktaAuth?: OktaAuth
): LoginHandlerResult => {
    const handleLogin = useCallback(
        async (accessToken: string): Promise<void> => {
            if (!accessToken) {
                Logger.error('No access token provided to login handler');
                navigate('/login');
                return;
            }

            try {
                // Fetch user details - will throw 404 if user not registered
                const user = await UserService.getUser(accessToken);

                // Save to Redux (localStorage middleware saves synchronously after dispatch)
                dispatch(setCredentialsAndUser({ user, credentials: { accessToken } }));

                // Force Okta to update its auth state immediately if oktaAuth is available
                if (oktaAuth) {
                    await oktaAuth.authStateManager.updateAuthState();
                }

                // Navigate to dashboard
                navigate('/');
            } catch (error) {
                // Check if it's a 404 - user authenticated but not registered
                if (error instanceof AxiosError && error.response?.status === 404) {
                    Logger.info('User authenticated in Okta but not registered. Clearing SSO session.');

                    // Clear Okta SSO session completely (logs out of all apps using this Okta tenant)
                    if (oktaAuth) {
                        await oktaAuth.signOut({
                            postLogoutRedirectUri: `${window.location.origin}/login`,
                        });
                        return; // signOut handles redirect
                    }

                    // Fallback if oktaAuth not available
                    navigate('/login');
                    return;
                }

                Logger.error(`Login handler error: ${String(error)}`);
                navigate('/login');
                throw error;
            }
        },
        [dispatch, navigate, oktaAuth]
    );

    return { handleLogin };
};
